/**
 * Dates, sizes, durations, numbers and language names in the interface's
 * language — not the browser's.
 *
 * `toLocaleString(undefined, …)` formats in the *browser's* locale, so a French
 * interface on an English browser printed "Oct 27, 2026, 2:05 PM" beside
 * French labels. Everything that formats for display goes through here.
 */
import { useMemo } from "react";
import { TRANSLATION_LANGUAGES, type Locale } from "@rec/shared";
import { currentLocale, useLocale } from "./index";

const BYTE_UNITS: Record<Locale, { kb: string; mb: string; gb: string; tb: string }> = {
  en: { kb: "KB", mb: "MB", gb: "GB", tb: "TB" },
  // French writes octets: Ko, Mo, Go, To.
  fr: { kb: "Ko", mb: "Mo", gb: "Go", tb: "To" },
};

export function makeFormatter(locale: Locale) {
  const number = (value: number, options?: Intl.NumberFormatOptions) =>
    new Intl.NumberFormat(locale, options).format(value);

  let displayNames: Intl.DisplayNames | null = null;
  try {
    displayNames = new Intl.DisplayNames([locale], { type: "language" });
  } catch {
    displayNames = null;
  }

  return {
    number,

    /** "27 oct. 2026" / "Oct 27, 2026". */
    date: (value: Date | string | number, options?: Intl.DateTimeFormatOptions) =>
      new Date(value).toLocaleDateString(locale, options ?? { dateStyle: "medium" }),

    /** Date and time; pass options to override the default medium/short. */
    dateTime: (value: Date | string | number, options?: Intl.DateTimeFormatOptions) =>
      new Date(value).toLocaleString(
        locale,
        options ?? { dateStyle: "medium", timeStyle: "short" },
      ),

    time: (value: Date | string | number, options?: Intl.DateTimeFormatOptions) =>
      new Date(value).toLocaleTimeString(locale, options ?? { timeStyle: "short" }),

    /** Same rungs as `formatBytes` in @rec/shared, with localized units and decimals. */
    bytes: (bytes: number) => {
      const units = BYTE_UNITS[locale];
      // Workspace totals in the back-office reach terabytes; "2048 GB" is
      // the same unreadable label as "0 MB" was at the other end.
      const tb = bytes / 1024 ** 4;
      if (tb >= 1) return `${number(Number(tb.toFixed(tb < 10 ? 1 : 0)))} ${units.tb}`;
      const gb = bytes / 1024 ** 3;
      if (gb >= 1) return `${number(Number(gb.toFixed(gb < 10 ? 1 : 0)))} ${units.gb}`;
      const mb = bytes / 1024 ** 2;
      if (mb >= 1) return `${number(Math.round(mb))} ${units.mb}`;
      return `${number(Math.max(1, Math.round(bytes / 1024)))} ${units.kb}`;
    },

    /**
     * Same rungs as `formatDuration` in @rec/shared. "s", "min" and "h" are
     * the same abbreviations in French, so only the numbers change.
     */
    duration: (ms: number) => {
      if (ms < 59_500) return `${Math.max(1, Math.round(ms / 1000))} s`;
      const totalMinutes = Math.round(ms / 60_000);
      if (totalMinutes < 60) return `${totalMinutes} min`;
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      return minutes === 0 ? `${hours} h` : `${hours} h ${minutes} min`;
    },

    /**
     * A language's name in the interface's language — "allemand", not
     * "German" — falling back to the English label the catalogue has.
     */
    languageName: (code: string) => {
      const name = displayNames?.of(code);
      if (name && name !== code) return name.charAt(0).toLocaleUpperCase(locale) + name.slice(1);
      return TRANSLATION_LANGUAGES.find((l) => l.code === code)?.label ?? code;
    },

    /** "il y a 3 minutes" / "3 minutes ago". */
    relative: (value: Date | string | number) => {
      const diffSeconds = Math.round((new Date(value).getTime() - Date.now()) / 1000);
      const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
      const steps: Array<[Intl.RelativeTimeFormatUnit, number]> = [
        ["year", 31_536_000],
        ["month", 2_592_000],
        ["week", 604_800],
        ["day", 86_400],
        ["hour", 3600],
        ["minute", 60],
      ];
      for (const [unit, seconds] of steps) {
        if (Math.abs(diffSeconds) >= seconds) return rtf.format(Math.round(diffSeconds / seconds), unit);
      }
      return rtf.format(diffSeconds, "second");
    },

    /** "A, B et C" / "A, B and C". */
    list: (items: string[], type: "conjunction" | "disjunction" = "conjunction") =>
      new Intl.ListFormat(locale, { style: "long", type }).format(items),
  };
}

export type Formatter = ReturnType<typeof makeFormatter>;

/** Formatting in the current language; re-renders when it changes. */
export function useFormat(): Formatter {
  const locale = useLocale();
  return useMemo(() => makeFormatter(locale), [locale]);
}

/** Outside React. */
export function format(): Formatter {
  return makeFormatter(currentLocale());
}
